import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from './ui/button';
import DOMPurify from 'dompurify';

const defaultLegalContent = {
  terms: {
    title: 'Terms and Conditions',
    content: `Wayo Platform Use Agreement

Last updated: 05 February 2026

1. Introduction

Welcome to Wayo ("Wayo", "we", "us", "our").

Wayo is an online directory platform that enables market vendors to create paid digital listings for their physical market stalls and enables members of the public to discover those vendors.

By accessing or using the platform, you agree to be bound by these Terms and Conditions. If you do not agree, you may not use the platform.

2. Definitions

Platform: The Wayo website, applications, and related services

Vendor: Any individual or business subscribing to list a market stall

Visitor: Any user browsing the platform

Listing: Vendor-submitted content including descriptions, images, and contact details

Subscription: A paid monthly listing access plan

Market: A physical, in-person trading environment

3. Nature of the Service

Wayo is a paid vendor directory and discovery platform only.

Wayo does not:

Facilitate sales or payments between vendors and customers

Act as an agent, broker, or intermediary

Verify vendors, products, or services

Guarantee availability, pricing, quality, legality, or compliance

All interactions, enquiries, and transactions occur independently and outside the platform.

4. Vendor Accounts and Listings

Vendors are solely responsible for all information submitted.

By creating a listing, vendors confirm that:

All information is accurate, lawful, and not misleading

They are authorised to represent the stall or business

They comply with all applicable laws, regulations, and market rules

Wayo reserves the right to edit, suspend, or remove listings at its sole discretion.

5. Prohibited Use (Robust – Expanded)

You may not, directly or indirectly, use the platform to:

5.1 Misrepresentation and Deception

Provide false, misleading, or incomplete information

Misrepresent products, pricing, availability, or market attendance

Claim unauthorised affiliations or endorsements

5.2 Illegal or Unlawful Activity

Promote or display illegal, restricted, or regulated goods or services

Breach consumer protection, health, safety, or trading laws

Infringe intellectual property rights

5.3 Harmful, Offensive, or Unsafe Content

Upload defamatory, abusive, hateful, discriminatory, or threatening content

Publish sexually explicit, violent, or inappropriate material

Promote exploitation, harassment, or unsafe practices

5.4 Platform Abuse and Technical Misuse

Interfere with platform operation or security

Scrape, copy, or harvest data without permission

Use bots, automation, or unauthorised scripts

5.5 Advertising and Monetisation Abuse

Attempt to manipulate, interfere with, or bypass advertising systems

Click on advertisements displayed on your own listings or encourage others to do so

Use misleading content to artificially increase ad impressions or clicks

Violate Google AdSense or third-party advertising policies

5.6 Impersonation and Fraud

Impersonate individuals, vendors, markets, or organisations

Create duplicate, deceptive, or fraudulent listings

Provide false identities or contact details

5.7 Circumvention of Enforcement

Bypass moderation or enforcement actions

Recreate accounts after suspension or termination

Assist others in violating these Terms

Violations may result in immediate suspension or permanent termination without notice and without refund.

6. Content Ownership and Licence

Vendors retain ownership of submitted content.

By submitting content, vendors grant Wayo a non-exclusive, worldwide, royalty-free licence to host, display, reproduce, and promote such content in connection with the platform and its marketing.

7. Advertising and Third-Party Monetisation (Google AdSense)

7.1 Advertising Display

Wayo displays third-party advertisements, including advertisements served by Google AdSense, across the platform.

Advertisements may appear:

On vendor listings

Within browsing pages

Adjacent to content

7.2 No Endorsement

The display of advertisements does not constitute endorsement by Wayo of any advertised product or service.

7.3 Cookies and Data Usage

Third-party advertisers, including Google, may:

Use cookies or similar technologies

Collect anonymised usage data

Serve ads based on user activity

Users consent to such data processing by continuing to use the platform. More information is provided in the Privacy Policy.

7.4 Advertising Compliance

Users and vendors may not:

Interfere with ad delivery

Attempt to influence ad performance

Block or obscure advertisements in a manner that violates platform rules

8. Subscriptions and Payments

8.1 Paid Listings

Vendor listings are available only via paid subscription at R25.00 per month.

8.2 Payment Processing

Payments are processed via PayFast

Wayo does not store payment information

Vendors agree to PayFast's terms in addition to these Terms

8.3 Billing and Failure

Subscriptions renew monthly in advance

Failed payments may result in listing suspension or removal

8.4 Cancellation

Vendors may cancel at any time

Cancellation takes effect at the end of the current billing cycle

No partial refunds are issued

8.5 Price Changes

Wayo may adjust subscription pricing with reasonable notice. Continued use constitutes acceptance.

9. No Warranty

The platform is provided "as is" and "as available".

Wayo makes no warranties regarding:

Listing visibility or traffic

Vendor performance or reliability

Market participation or availability

10. Limitation of Liability

To the maximum extent permitted by law:

Wayo is not liable for disputes, losses, or damages arising from vendor-customer interactions

Wayo is not responsible for vendor conduct, products, or services

11. Suspension and Termination

Wayo may suspend or terminate access at any time if:

These Terms are breached

Advertising or payment systems are abused

Required by law

12. Governing Law

These Terms are governed by the laws of the Republic of South Africa.`
  },
  privacy: {
    title: 'Privacy Policy',
    content: `Privacy Policy (POPIA & Advertising Aligned)

1. Overview

Wayo processes personal information in accordance with the Protection of Personal Information Act (POPIA).

2. Information Collected

Vendors:

Business and contact details

Listing content and images

Visitors:

Usage and analytics data

Device and browser information

3. Advertising Data

Third-party advertising partners, including Google AdSense, may collect anonymised data via cookies to serve relevant advertisements.

Wayo does not control third-party data collection practices.

4. Purpose of Processing

Information is used to:

Operate the platform

Display listings and advertisements

Improve user experience

Comply with legal obligations

5. Data Sharing

Personal information is not sold or rented.

Limited sharing occurs only with:

Platform service providers

Advertising partners

Legal authorities when required

6. Security

Appropriate safeguards are implemented to protect personal information.

7. User Rights

Users may request access, correction, or deletion of personal data as permitted by law.

8. Cookies

Wayo uses functional, analytical, and advertising cookies. Continued use constitutes consent.`
  },
  other: {
    title: 'Other Legal Information',
    content: `Signup

By creating an account, you agree to Wayo's Terms & Privacy Policy. Wayo is a paid vendor directory, not a sales platform.

Subscription Checkout

You are subscribing to a monthly vendor listing for R25.00. Payments are processed securely via PayFast.

Advertising Notice

This platform displays third-party advertisements, including Google AdSense.

Listing Disclaimer

Vendor information is provided by third parties. Wayo does not verify or endorse vendors or products.`
  }
};

const LegalModal = ({ type, isOpen, onClose }) => {
  const [legalContent, setLegalContent] = useState(defaultLegalContent);
  const [loading, setLoading] = useState(false);
  const API_URL = window.location.origin;

  useEffect(() => {
    if (isOpen && type) {
      fetchLegalContent();
    }
  }, [isOpen, type]);

  const fetchLegalContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/legal/public/${type}`);
      if (response.ok) {
        const data = await response.json();
        if (data.title && data.content) {
          setLegalContent(prev => ({
            ...prev,
            [type]: {
              title: data.title,
              content: data.content
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching legal content:', error);
      // Fall back to default content
    } finally {
      setLoading(false);
    }
  };

  if (!type || !legalContent[type]) return null;
  
  const { title, content } = legalContent[type];

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-50 w-[95vw] max-w-3xl bg-white dark:bg-[#1a1a1a] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl max-h-[90vh] overflow-hidden border-4 border-[#FFD12D] flex flex-col">
          
          {/* Header with Back Button */}
          <div className="relative bg-gradient-to-r from-[#FFD12D] to-[#b8960f] py-6 px-4">
            {/* Back Button */}
            <Button
              onClick={onClose}
              variant="ghost"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 p-0 rounded-full bg-white hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] shadow-lg transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#252525] text-center px-14">
              {title}
            </h2>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-8">
            <div className="prose prose-gray dark:prose-invert max-w-none legal-content">
              {content.includes('<') ? (
                // HTML content from rich text editor - sanitized to prevent XSS
                <div 
                  className="text-gray-700 dark:text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
                />
              ) : (
                // Plain text content (fallback/default)
                content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#252525]">
            <Button
              onClick={onClose}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-4 transition-all duration-300"
            >
              Close
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default LegalModal;
